import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiResponse } from "../../../types/APIResponse";
import api, { apiWithFormDataMethods } from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { AdminSession, AdminUser, CreateUserRole, DeleteUserRole, Establishment, GetAllUserRolesResponse, UserPermissionConfig } from "./types";
import { RootState } from "../../../redux/store";

// Async get all user permission configuration
export const getAllPermissionConfig = createAsyncThunk<UserPermissionConfig[], void, { rejectValue: string }>("user/getAllPermissionConfig", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get<ApiResponse<UserPermissionConfig[]>>(apiEndpoints.user.getAllPermissionConfig);
    return response.data; // Access the data field containing the user permissions
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch permissions");
  }
});

// Create a user role config
export const createRole = createAsyncThunk<GetAllUserRolesResponse, { data: CreateUserRole }, { rejectValue: string }>("user/createRole", async ({ data }, { rejectWithValue }) => {
  try {
    const response = await api.post<ApiResponse<GetAllUserRolesResponse>>(apiEndpoints.user.createRole, { data });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create role");
  }
});

export const updateRole = createAsyncThunk<GetAllUserRolesResponse, { id: string; data: CreateUserRole }, { rejectValue: string }>("user/updateRole", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.put<ApiResponse<GetAllUserRolesResponse>>(`${apiEndpoints.user.updateRole}/${id}`, { data });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update role");
  }
});

// Async get all roles
export const getAllRolesConfig = createAsyncThunk<GetAllUserRolesResponse[], void, { rejectValue: string }>("user/getAllRolesConfig", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get<ApiResponse<GetAllUserRolesResponse[]>>(apiEndpoints.user.getAllRole);
    return response.data; // Access the data field containing the user roles
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch roles");
  }
});

//Delete User Role
export const deleteUserRole = createAsyncThunk<DeleteUserRole, { id: string }, { rejectValue: string }>("user/deleteUserRole", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await api.delete<ApiResponse<DeleteUserRole>>(`${apiEndpoints.user.deleteRole}/${id}`);
    return { ...response.data, id }; // Access the data field containing the deleted user role
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to delete user role");
  }
});

// Create a admin user
export const createAdminUser = createAsyncThunk<AdminUser, { data: AdminUser }, { rejectValue: string }>("user/createAdminUser", async ({ data }, { rejectWithValue }) => {
  try {
    const response = await api.post<ApiResponse<AdminUser>>(apiEndpoints.user.createAdminUser, { data });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create admin user");
  }
});

// Update admin user
export const updateAdminUser = createAsyncThunk<AdminUser, { id: string; data: AdminUser }, { rejectValue: string }>("user/updateAdminUser", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.put<ApiResponse<AdminUser>>(`${apiEndpoints.user.updateAdminUser}/${id}`, { data });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create admin user");
  }
});

// Delete admin user
export const deleteAdminUser = createAsyncThunk<{ id: string }, { id: string }, { rejectValue: string }>("user/deleteAdminUser", async ({ id }, { rejectWithValue }) => {
  try {
    await api.delete<ApiResponse<null>>(`${apiEndpoints.user.deleteAdminUser}/${id}`);
    return { id };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to delete admin user");
  }
});

// get all admin users
export const getAllAdminUser = createAsyncThunk<{ data: AdminUser[]; counts: number; limit: number }, { offset: number; limit: number; searchField?: any; searchValue: any }, { rejectValue: string }>(
  "user/getAllAdminUser",
  async ({ offset, limit, searchField, searchValue }, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<AdminUser[]> & { counts: number; limit: number }>(apiEndpoints.user.getAllAdminUser, {
        data: {
          offset,
          limit,
          searchField, // Pass searchField if provided
          searchValue, // Pass searchValue if provided
          order: { "admin.createdAt": "DESC" },
        },
      });
      return {
        data: response.data, // Access nested data from the response
        counts: response.counts,
        limit: response.limit,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch admin users");
    }
  }
);

//get All Case Manager
export const getAllCaseManager = createAsyncThunk<{ data: AdminUser[] }, void, { rejectValue: string }>("user/getAllCaseManager", async (_, { rejectWithValue }) => {
  try {
    const response = await api.post<ApiResponse<AdminUser[]>>(apiEndpoints.user.getAllCaseManager);
    return {
      data: response.data,
    };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch admin users");
  }
});

// Create permission
export const createPermission = createAsyncThunk<UserPermissionConfig, { data: Omit<UserPermissionConfig, "id" | "createdAt" | "updatedAt" | "deletedAt"> }, { rejectValue: string }>(
  "user/createPermission",
  async ({ data }, { rejectWithValue, getState }) => {
    try {
      // First create the new permission
      const response = await api.post<ApiResponse<UserPermissionConfig>>(apiEndpoints.permission.create, { data });
      if (!response?.success) {
        throw new Error(response.message || "Failed to create permission");
      }

      // Get permissions from Redux state
      const state = getState() as RootState;
      const existingPermissions = state.users.allUserPermissions;

      // Filter permissions of same type and update their indices
      const sameTypePermissions = existingPermissions.filter(p => p.configType === data.configType);
      const updatedPermissions = sameTypePermissions.map(permission => ({
        id: permission.id,
        configType: permission.configType,
        index: permission.index + 1 // Increment all existing indices by 1
      }));

      // Update all permissions with new indices
      const updateResponse = await api.post<ApiResponse<UserPermissionConfig[]>>(apiEndpoints.permission.updateOrder, {
        data: { orderUpdates: updatedPermissions }
      });

      if (!updateResponse?.success) {
        throw new Error(updateResponse.message || "Failed to update permission indices");
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create permission");
    }
  }
);

// Update permission
export const updatePermission = createAsyncThunk<UserPermissionConfig, { id: string; data: Partial<UserPermissionConfig> }, { rejectValue: string }>("user/updatePermission", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.put<ApiResponse<UserPermissionConfig>>(`${apiEndpoints.permission.update}/${id}`, { data });
    if (response?.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update permission");
    }
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update permission");
  }
});

// Update permission order
export const updatePermissionOrder = createAsyncThunk<UserPermissionConfig[], { orderUpdates: Array<{ id: string; configType: number; index: number }> }, { rejectValue: string }>(
  "user/updatePermissionOrder",
  async ({ orderUpdates }, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<UserPermissionConfig[]>>(apiEndpoints.permission.updateOrder, { data: { orderUpdates } });
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to update permission order");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update permission order");
    }
  }
);

// Delete permission
export const deletePermission = createAsyncThunk<{ id: string }, { id: string }, { rejectValue: string }>("user/deletePermission", async ({ id }, { rejectWithValue }) => {
  try {
    await api.delete<ApiResponse<null>>(`${apiEndpoints.permission.delete}/${id}`);
    return { id };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to delete permission");
  }
});

// Create establishment
export const updateEstablishment = createAsyncThunk<Establishment, { formData: FormData }, { rejectValue: string }>(
  "user/updateEstablishment",
  async ({ formData }, { rejectWithValue }) => {
    try {
      const response = await apiWithFormDataMethods.put<ApiResponse<Establishment>>(apiEndpoints.establishment.update, formData);
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to update establishment");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create establishment");
    }
  }
);

//Get establishment
export const getEstablishment = createAsyncThunk<Establishment, void, { rejectValue: string }>(
  "user/getEstablishment",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<Establishment>>(apiEndpoints.establishment.get);
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to get establishment");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to get establishment");
    }
  }
);

// Get all sessions for a user
export const getUserSessions = createAsyncThunk<AdminSession[], { adminId: string }, { rejectValue: string }>(
  "user/getUserSessions",
  async ({ adminId }, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<AdminSession[]>>(`${apiEndpoints.adminSession.getUserSessions}/${adminId}`);
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch sessions");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch sessions");
    }
  }
);

// Approve a session
export const approveSession = createAsyncThunk<AdminSession, { sessionId: string }, { rejectValue: string }>(
  "user/approveSession",
  async ({ sessionId }, { rejectWithValue }) => {
    try {
      const response = await api.put<ApiResponse<AdminSession>>(`${apiEndpoints.adminSession.approve}/${sessionId}/approve`);
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to approve session");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to approve session");
    }
  }
);

// Disapprove a session
export const disapproveSession = createAsyncThunk<AdminSession, { sessionId: string }, { rejectValue: string }>(
  "user/disapproveSession",
  async ({ sessionId }, { rejectWithValue }) => {
    try {
      const response = await api.put<ApiResponse<AdminSession>>(`${apiEndpoints.adminSession.disapprove}/${sessionId}/disapprove`);
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to disapprove session");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to disapprove session");
    }
  }
);

// Revoke a session
export const revokeSession = createAsyncThunk<{ sessionId: string }, { sessionId: string }, { rejectValue: string }>(
  "user/revokeSession",
  async ({ sessionId }, { rejectWithValue }) => {
    try {
      const response = await api.delete<ApiResponse<null>>(`${apiEndpoints.adminSession.revoke}/${sessionId}`);
      if (response?.success) {
        return { sessionId };
      } else {
        throw new Error(response.message || "Failed to revoke session");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to revoke session");
    }
  }
);